/**
 * Utility functions for printing PDFs using native browser functionality
 */

/**
 * Derives a download filename from a PDF URL by extracting the base filename
 * and appending the language suffix (e.g. "ABC123_en.pdf" or "ABC123_es.pdf").
 *
 * Falls back to "report" as the base name if the URL contains no recognisable
 * filename segment.
 */
export const getReportFilename = (
  pdfUrl: string,
  languageSuffix: string,
): string => {
  const rawFilename = pdfUrl.split("/").pop()?.split("?")[0] || "report.pdf";
  const baseName = rawFilename.replace(/\.pdf$/i, "");
  return `${baseName}_${languageSuffix}.pdf`;
};

export const downloadPdfFromBlob = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  const tempAnchor = document.createElement("a");

  tempAnchor.style.display = "none";
  tempAnchor.download = filename;
  tempAnchor.href = url;

  document.body.appendChild(tempAnchor);
  tempAnchor.click();
  document.body.removeChild(tempAnchor);

  URL.revokeObjectURL(url);
};

export const printPdfFromBlob = (
  blob: Blob,
  filename?: string,
  physicalDeviceType?: string,
): Promise<void> =>
  new Promise((resolve) => {
    try {
      const shouldDownload =
        physicalDeviceType !== "desktop" ||
        (navigator.userAgent.toLowerCase().includes("safari") &&
          navigator.userAgent.toLowerCase().includes("mobile"));

      if (shouldDownload) {
        downloadPdfFromBlob(blob, filename || "document.pdf");
        resolve();
        return;
      }

      const url = URL.createObjectURL(blob);
      const newTab = window.open(url, "_blank");

      if (!newTab) {
        downloadPdfFromBlob(blob, filename || "document.pdf");
      } else {
        setTimeout(() => {
          newTab.onload = () => {
            try {
              newTab.print();
            } catch (e) {
              // Print failed, but tab is still open for manual printing
            }
          };
        }, 1000);

        setTimeout(() => {
          try {
            if (!newTab.closed) {
              newTab.print();
            }
          } catch (e) {
            // Silent fail - user can still print manually
          }
        }, 2000);
      }

      setTimeout(() => {
        URL.revokeObjectURL(url);
      }, 10000);

      resolve();
    } catch (error) {
      downloadPdfFromBlob(blob, filename || "document.pdf");
      resolve();
    }
  });
